/**
 * @fileoverview A tenant's current target query hook.
 * @description Wraps GET /sales/targets?tenantId=... in a TanStack query, returning the single
 * target the data model currently allows per tenant (see tenant.types.ts's Target doc comment on
 * why only one open target per tenant is supported today). Disabled while `tenantId` is falsy, same
 * reasoning as useTenant.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { TARGETS_QUERY_KEY } from '@/features/sales/hooks/useTargets'
import { getTargets } from '@/features/sales/services/targets.service'

/**
 * @description Fetches the target(s) recorded against one tenant.
 * @param {string} tenantId - The tenant id to fetch targets for, or an empty string to keep the
 * query disabled.
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, error, ...); `data` is
 * the tenant's target list (ordered oldest-first by the mock store), typically 0 or 1 entries.
 */
export function useTenantTarget(tenantId: string) {
  return useQuery({
    queryKey: [...TARGETS_QUERY_KEY, 'byTenant', tenantId],
    queryFn: () => getTargets({ tenantId }),
    enabled: tenantId !== '',
  })
}
